import { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageMetaState } from '../../hooks/usePageMeta';
import { useMenu } from '../../contexts/MenuContext';
import { FavoriteStar } from './FavoriteStar';
import './Topbar.css';

// Aktif rotayı menü ağacında bulup KÖK→YAPRAK başlık zincirini döner (breadcrumb için).
// Kaynak: NavMenu'nün yayınladığı resolvedMenus (çizilen ağaç) + API menuItems birleşimi.
// Eşleşme: tam path; yoksa pathname'in ön eki olan en UZUN menü path'i (detay rotaları için).
function buildMenuTrail(pathname, resolved, base) {
  const all = [...(base || []), ...(resolved || [])];
  if (!all.length) return null;
  const byId = new Map();
  all.forEach((m) => { if (m && m.id != null) byId.set(m.id, m); });
  let match = null; let bestLen = -1;
  all.forEach((m) => {
    if (!m || !m.path || m.path === '#') return;
    if (m.path === pathname) { if (bestLen < 1e9) { match = m; bestLen = 1e9; } return; }
    if (pathname.startsWith(`${m.path}/`)) { if (m.path.length > bestLen) { match = m; bestLen = m.path.length; } }
  });
  if (!match) return null;
  const trail = [];
  let cur = match; let guard = 0;
  while (cur && guard < 12) { if (cur.title) trail.unshift(cur.title); cur = cur.parentID ? byId.get(cur.parentID) : null; guard += 1; }
  return trail;
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10.9 18.1a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M20.3 20.3l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/**
 * @param searchSlot  Arama kutusunun tamamını devralan bileşen (bkz. GlobalSearch). Verilirse
 *                    Topbar kendi input'unu çizmez — arama davranışı tek bir yerde toplanır.
 */
export function Topbar({ searchPlaceholder, onSearchChange, searchValue, actions, searchSlot }) {
  const { title, subtitle } = usePageMetaState();
  const { pathname } = useLocation();
  const { resolvedMenus, menuItems } = useMenu();
  // Tam menü yolu (Anasayfa › … › mevcut). Menüde eşleşme yoksa sayfa başlığına düşer.
  const trail = useMemo(() => {
    const t = buildMenuTrail(pathname, resolvedMenus, menuItems);
    return (t && t.length) ? t : (title ? [title] : []);
  }, [pathname, resolvedMenus, menuItems, title]);
  const controlled = searchValue !== undefined;
  const [internalValue, setInternalValue] = useState('');
  const value = controlled ? searchValue : internalValue;

  const handleChange = (e) => {
    const next = e.target.value;
    if (!controlled) setInternalValue(next);
    if (onSearchChange) onSearchChange(next);
  };

  return (
    <header className="ui-topbar">
      <div className="ui-topbar-left">
        {/* Breadcrumb: prototip topbar'ındaki gibi büyük başlığın üstünde bağlam yolu. */}
        <div className="ui-topbar-crumb" aria-hidden="true">
          <span className="ui-topbar-crumb-home">Anasayfa</span>
          {trail.map((t, i) => (
            <span key={`${t}-${i}`}>
              <span className="ui-topbar-crumb-sep">›</span>
              <span className={i === trail.length - 1 ? 'ui-topbar-crumb-cur' : 'ui-topbar-crumb-anc'}>{t}</span>
            </span>
          ))}
        </div>
        {/* Başlık + Favorilerim yıldızı: açık sayfayı sol menüde en üste sabitler. */}
        <div className="ui-topbar-title-row">
          <div className="ui-topbar-title">{title || ''}</div>
          <FavoriteStar />
        </div>
        {subtitle && <div className="ui-topbar-sub">{subtitle}</div>}
      </div>
      {/* Arama kendi grid kolonunda: sol başlık ve sağ aksiyonlar ne kadar geniş olursa olsun
          kutu topbar'ın ortasında sabit kalır. */}
      <div className="ui-topbar-center">
        {searchSlot ?? (
          <label className="ui-topbar-search">
            <SearchIcon />
            <input
              type="search"
              placeholder={searchPlaceholder || 'Ara…'}
              value={value}
              onChange={handleChange}
              aria-label={searchPlaceholder || 'Search'}
            />
          </label>
        )}
      </div>
      <div className="ui-topbar-right">
        {actions && <div className="ui-topbar-actions">{actions}</div>}
      </div>
    </header>
  );
}
